// Skater materials — every per-skater surface, all instanced (one draw call
// per part for the whole roster). Team colors and per-frame state ride
// per-instance attributes created by SkatersView and referenced here via
// `instancedBufferAttribute`; style tunables are uniforms.
//
// Parts: energy dome (fresnel shell), ground ring decal (team ring +
// selection ring + protect pulse in one shader), direction fin, control
// node, tether, comet trail, charge arc decal, stun star.

import {
  atan,
  clamp,
  float,
  instancedBufferAttribute,
  mix,
  normalView,
  positionViewDirection,
  smoothstep,
  time,
  uniform,
  uv,
  vec3,
} from "three/tsl";
import {
  AdditiveBlending,
  Color,
  DoubleSide,
  type InstancedBufferAttribute,
  MeshBasicNodeMaterial,
} from "three/webgpu";
import type { EntitiesStyle } from "../style/style-schema.js";

// biome-ignore lint/suspicious/noExplicitAny: TSL graph-building values
type AnyNode = any;

export type SkaterAttributes = {
  /** Per-instance team core color (set once). */
  aCore: InstancedBufferAttribute;
  /** Per-instance team glow color (set once). */
  aGlow: InstancedBufferAttribute;
  /** Per-instance team rim color (set once). */
  aRim: InstancedBufferAttribute;
  /** Per-frame state: x = selected, y = protecting, z = chargeFraction, w = trailAlpha. */
  aState: InstancedBufferAttribute;
};

function buildUniforms(style: EntitiesStyle) {
  return {
    domeAlpha: uniform(style.skater.domeAlpha),
    domeIntensity: uniform(style.skater.domeIntensity),
    rimPower: uniform(style.skater.rimPower),
    ringInnerFrac: uniform(style.skater.ringInnerFrac),
    ringIntensity: uniform(style.skater.ringIntensity),
    finIntensity: uniform(style.skater.finIntensity),
    nodeIntensity: uniform(style.skater.nodeIntensity),
    tetherIntensity: uniform(style.skater.tetherIntensity),
    trailIntensity: uniform(style.trail.intensity),
    selectionColor: uniform(new Color(style.indicators.selectionColor)),
    selectionIntensity: uniform(style.indicators.selectionIntensity),
    protectColor: uniform(new Color(style.indicators.protectColor)),
    protectPulseHz: uniform(style.indicators.protectPulseHz),
    protectIntensity: uniform(style.indicators.protectIntensity),
    chargeStartColor: uniform(new Color(style.indicators.chargeStartColor)),
    chargeEndColor: uniform(new Color(style.indicators.chargeEndColor)),
    chargeIntensity: uniform(style.indicators.chargeIntensity),
    stunColor: uniform(new Color(style.indicators.stunColor)),
    stunIntensity: uniform(style.indicators.stunIntensity),
  };
}

export type SkaterUniforms = ReturnType<typeof buildUniforms>;

export function applySkaterStyle(u: SkaterUniforms, s: EntitiesStyle): void {
  u.domeAlpha.value = s.skater.domeAlpha;
  u.domeIntensity.value = s.skater.domeIntensity;
  u.rimPower.value = s.skater.rimPower;
  u.ringInnerFrac.value = s.skater.ringInnerFrac;
  u.ringIntensity.value = s.skater.ringIntensity;
  u.finIntensity.value = s.skater.finIntensity;
  u.nodeIntensity.value = s.skater.nodeIntensity;
  u.tetherIntensity.value = s.skater.tetherIntensity;
  u.trailIntensity.value = s.trail.intensity;
  u.selectionColor.value.set(s.indicators.selectionColor);
  u.selectionIntensity.value = s.indicators.selectionIntensity;
  u.protectColor.value.set(s.indicators.protectColor);
  u.protectPulseHz.value = s.indicators.protectPulseHz;
  u.protectIntensity.value = s.indicators.protectIntensity;
  u.chargeStartColor.value.set(s.indicators.chargeStartColor);
  u.chargeEndColor.value.set(s.indicators.chargeEndColor);
  u.chargeIntensity.value = s.indicators.chargeIntensity;
  u.stunColor.value.set(s.indicators.stunColor);
  u.stunIntensity.value = s.indicators.stunIntensity;
}

export type SkaterMaterials = ReturnType<typeof createSkaterMaterials>;

export function createSkaterMaterials(style: EntitiesStyle, attrs: SkaterAttributes) {
  const u = buildUniforms(style);
  const core = instancedBufferAttribute(attrs.aCore) as AnyNode;
  const glow = instancedBufferAttribute(attrs.aGlow) as AnyNode;
  const rim = instancedBufferAttribute(attrs.aRim) as AnyNode;
  const state = instancedBufferAttribute(attrs.aState) as AnyNode;

  // --- Energy dome: translucent shell, fresnel-graded glow→core→rim ------
  const dome = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false });
  {
    const fres = normalView.dot(positionViewDirection).abs().oneMinus().pow(u.rimPower);
    const shellColor = mix(glow, core, fres).add(rim.mul(fres.mul(fres)));
    dome.colorNode = shellColor.mul(u.domeIntensity);
    dome.opacityNode = mix(float(u.domeAlpha), float(1), fres);
  }

  // --- Ground ring decal: team ring + selection + protect in one quad ----
  // Quad spans r 0..1; team ring sits at [ringInnerFrac·s, s] where s ≈ 0.72
  // leaves room for the selection (0.82) and protect (0.95) annuli outside.
  const ring = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false });
  {
    const r = uv().sub(0.5).mul(2).length();
    const band = (center: AnyNode | number, halfW: number): AnyNode =>
      smoothstep(0.025, 0.0, r.sub(center).abs().sub(halfW));
    const teamMid = u.ringInnerFrac.mul(0.72).add(0.72).mul(0.5);
    const teamHalf = float(0.72).sub(u.ringInnerFrac.mul(0.72)).mul(0.5);
    const teamRing = smoothstep(0.025, 0.0, r.sub(teamMid).abs().sub(teamHalf));
    const selRing = band(0.84, 0.018).mul(state.x);
    const protectPulse = time
      .mul(u.protectPulseHz.mul(Math.PI * 2))
      .sin()
      .mul(0.25)
      .add(0.55);
    const protRing = band(0.96, 0.02).mul(state.y).mul(protectPulse);
    const colorSum = core
      .mul(teamRing.mul(u.ringIntensity))
      .add(u.selectionColor.mul(selRing.mul(u.selectionIntensity)))
      .add(u.protectColor.mul(protRing.mul(u.protectIntensity)));
    ring.colorNode = colorSum;
    ring.opacityNode = clamp(teamRing.add(selRing).add(protRing), 0, 1);
  }

  // --- Solid emissive parts ----------------------------------------------
  const fin = new MeshBasicNodeMaterial();
  fin.colorNode = core.mul(u.finIntensity);

  const node = new MeshBasicNodeMaterial();
  node.colorNode = rim.mul(u.nodeIntensity);

  const tether = new MeshBasicNodeMaterial({
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
  });
  tether.colorNode = vec3(1, 1, 1).mul(u.tetherIntensity);
  tether.opacityNode = float(0.3);

  // --- Comet trail: additive, fades toward the tail tip ------------------
  const trail = new MeshBasicNodeMaterial({
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    side: DoubleSide,
  });
  {
    const fade = uv().y.oneMinus();
    trail.colorNode = core.mul(u.trailIntensity);
    trail.opacityNode = fade.mul(fade).mul(state.w);
  }

  // --- Charge arc decal: radial progress ring around the skater ----------
  const charge = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false });
  {
    const p = uv().sub(0.5).mul(2);
    const r = p.length();
    const annulus = smoothstep(0.03, 0.0, r.sub(0.86).abs().sub(0.09));
    // Angle from -z (screen-up at the default camera), 0..1 clockwise.
    const angle = atan(p.x, p.y.negate())
      .div(Math.PI * 2)
      .add(0.5);
    const filled = smoothstep(0.0, 0.01, state.z.sub(angle));
    const chargeColor = mix(u.chargeStartColor, u.chargeEndColor, state.z);
    charge.colorNode = chargeColor.mul(u.chargeIntensity);
    charge.opacityNode = annulus.mul(filled).mul(smoothstep(0.0, 0.01, state.z));
  }

  // --- Stun star ----------------------------------------------------------
  const star = new MeshBasicNodeMaterial({ side: DoubleSide });
  star.colorNode = u.stunColor.mul(u.stunIntensity);

  return { u, dome, ring, fin, node, tether, trail, charge, star };
}
